import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ChevronDown, ChevronLeft, Search, X, Star } from 'lucide-react';
import { useMarketData } from '../../contexts/MarketDataContext';
import { TOP_CRYPTO_PAIRS, CFD_INSTRUMENTS } from '../../constants/tradingPairs';
import { getListedCfdInstrument, matchesCfdSearch } from '../../../supabase/functions/_shared/cfdInstruments';
import ListedCfdLogo from '../ListedCfdLogo';

interface MobileTradingHeaderProps {
  selectedPair: string;
  tradingMode: 'futures' | 'cfd';
  onPairChange: (pair: string) => void;
  onBackClick?: () => void;
  showBackButton?: boolean;
}

const MobileTradingHeader: React.FC<MobileTradingHeaderProps> = ({
  selectedPair,
  tradingMode,
  onPairChange,
  onBackClick,
  showBackButton = false
}) => {
  const { getPriceBySymbol, marketData } = useMarketData();
  const [fundingCountdown, setFundingCountdown] = useState('');
  const [showPairSelector, setShowPairSelector] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentPrice = getPriceBySymbol(selectedPair);

  const pairData = useMemo(() => {
    const data = marketData?.find(d => d.symbol === selectedPair);
    return data || null;
  }, [marketData, selectedPair]);

  const priceChange24h = pairData?.change_24h || 0;
  const high24h = pairData?.high_price_24h || currentPrice * 1.02;
  const low24h = pairData?.low_price_24h || currentPrice * 0.98;
  const volume24h = pairData?.volume_24h || 0;
  const fundingRate = -0.00369;

  useEffect(() => {
    const calculateFundingCountdown = () => {
      const now = new Date();
      const hours = now.getUTCHours();
      const nextFundingHour = Math.ceil(hours / 8) * 8;
      const targetHour = nextFundingHour >= 24 ? 0 : nextFundingHour;

      const target = new Date(now);
      target.setUTCHours(targetHour, 0, 0, 0);
      if (target <= now) {
        target.setUTCDate(target.getUTCDate() + 1);
      }

      const diff = target.getTime() - now.getTime();
      const h = Math.floor(diff / (1000 * 60 * 60));
      const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const s = Math.floor((diff % (1000 * 60)) / 1000);

      return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    };

    setFundingCountdown(calculateFundingCountdown());
    const interval = setInterval(() => {
      setFundingCountdown(calculateFundingCountdown());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowPairSelector(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const allPairs = useMemo(() => {
    if (tradingMode === 'cfd') {
      return CFD_INSTRUMENTS.map(p => ({ ...p }));
    }
    return TOP_CRYPTO_PAIRS.map(p => ({ ...p }));
  }, [tradingMode]);

  const filteredPairs = useMemo(() => {
    if (!searchTerm.trim()) return allPairs;
    const term = searchTerm.toLowerCase();
    return allPairs.filter(pair =>
      matchesCfdSearch(pair, term)
    );
  }, [allPairs, searchTerm]);

  const handlePairSelect = (symbol: string) => {
    onPairChange(symbol);
    setShowPairSelector(false);
    setSearchTerm('');
  };

  const formatPrice = (price: number) => {
    if (price >= 1000) return price.toFixed(2);
    if (price >= 1) return price.toFixed(4);
    if (price >= 0.0001) return price.toFixed(6);
    return price.toFixed(8);
  };

  const formatVolume = (vol: number) => {
    if (vol >= 1e9) return (vol / 1e9).toFixed(2) + 'B';
    if (vol >= 1e6) return (vol / 1e6).toFixed(2) + 'M';
    if (vol >= 1e3) return (vol / 1e3).toFixed(2) + 'K';
    return vol.toFixed(2);
  };

  const baseAsset = selectedPair.replace('USDT', '').replace('/USD', '').replace('USD', '');
  const isPositive = priceChange24h >= 0;

  const getPairPrice = (symbol: string) => {
    const price = getPriceBySymbol(symbol);
    return price > 0 ? price : 0;
  };

  const getPairChange = (symbol: string) => {
    const data = marketData?.find(d => d.symbol === symbol);
    return data?.change_24h || 0;
  };

  return (
    <div className="bg-white px-4 py-3 flex-shrink-0 border-b border-gray-200">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2" ref={dropdownRef}>
          {showBackButton && (
            <button onClick={onBackClick} className="p-1 -ml-2">
              <ChevronLeft size={24} className="text-gray-700" />
            </button>
          )}
          <button
            onClick={() => setShowPairSelector(!showPairSelector)}
            className="flex items-center gap-1"
          >
            <ListedCfdLogo symbol={selectedPair} className="w-7 h-7 mr-1" />
            <span className="text-gray-900 font-bold text-lg">{selectedPair}</span>
            <span className="text-gray-500 text-xs ml-1">{tradingMode === 'cfd' ? 'CFD' : 'Perp'}</span>
            <ChevronDown size={16} className="text-gray-500" />
          </button>

          {showPairSelector && (
            <div className="fixed inset-0 bg-white z-50 flex flex-col">
              <div className="flex items-center gap-3 p-4 border-b border-gray-200">
                <button onClick={() => setShowPairSelector(false)}>
                  <ChevronLeft size={24} className="text-gray-700" />
                </button>
                <div className="flex-1 relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full bg-gray-100 text-gray-900 pl-10 pr-10 py-2.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    autoFocus
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto">
                {filteredPairs.map((pair) => {
                  const price = getPairPrice(pair.symbol);
                  const change = getPairChange(pair.symbol);
                  const isSelected = selectedPair === pair.symbol;
                  const changePositive = change >= 0;

                  return (
                    <button
                      key={pair.symbol}
                      onClick={() => handlePairSelect(pair.symbol)}
                      className={`w-full flex items-center justify-between px-4 py-3 border-b border-gray-100 ${
                        isSelected ? 'bg-blue-50' : ''
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Star size={16} className="text-gray-300" />
                        <ListedCfdLogo symbol={pair.symbol} />
                        <div className="text-left">
                          <div className="text-gray-900 font-medium">{pair.symbol}</div>
                          <div className="text-gray-500 text-xs">{pair.name}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-gray-900 font-mono text-sm">
                          {price > 0 ? formatPrice(price) : '--'}
                        </div>
                        <div className={`font-mono text-xs ${changePositive ? 'text-green-600' : 'text-red-600'}`}>
                          {changePositive ? '+' : ''}{change.toFixed(2)}%
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="text-right text-xs">
          <div className="flex items-center gap-4 text-gray-500">
            <div>
              <span>24h High</span>
              <div className="text-gray-900">{formatPrice(high24h)}</div>
            </div>
            <div>
              <span>24h Vol({baseAsset})</span>
              <div className="text-gray-900">{formatVolume(volume24h)}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-end justify-between mt-2">
        <div>
          <div className={`text-2xl font-bold ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
            {formatPrice(currentPrice)}
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-gray-500">{getListedCfdInstrument(selectedPair)?.type === 'index' ? `${formatPrice(currentPrice)} pts` : `= $${formatPrice(currentPrice)}`}</span>
            <span className={`${isPositive ? 'text-green-600' : 'text-red-600'}`}>
              {isPositive ? '+' : ''}{priceChange24h.toFixed(2)}%
            </span>
          </div>
          <div className="text-gray-500 text-xs mt-0.5">
            Mark Price {formatPrice(currentPrice)}
          </div>
        </div>

        <div className="text-right text-xs">
          <div className="flex items-center gap-4 text-gray-500">
            <div>
              <span>24h Low</span>
              <div className="text-gray-900">{formatPrice(low24h)}</div>
            </div>
            <div>
              <span>24h Vol(USDT)</span>
              <div className="text-gray-900">{formatVolume(volume24h * currentPrice)}</div>
            </div>
          </div>
        </div>
      </div>

      {tradingMode === 'futures' && (
        <div className="flex items-center gap-2 mt-2 text-xs">
          <span className="text-gray-500">Funding (8h) / Countdown</span>
          <span className={fundingRate >= 0 ? 'text-green-600' : 'text-red-600'}>
            {fundingRate >= 0 ? '+' : ''}{(fundingRate).toFixed(5)}%
          </span>
          <span className="text-gray-900">/ {fundingCountdown}</span>
        </div>
      )}
    </div>
  );
};

export default MobileTradingHeader;
